/**
 * CRM adapter factory.
 *
 * Reads `tenant.config.json.crm.type` and returns the correct adapter wired in
 * proxy mode for the browser. Server-side callers (Netlify Functions) should
 * construct adapters directly with a decrypted accessToken — see `hubspot.ts` /
 * `attio.ts` for their `{ mode: 'direct', accessToken }` transports.
 *
 * `getCrmAdapter()` is memoised — same tenant config → same instance. Calling it
 * before CRM type is set in the wizard returns `null`, so callers must guard.
 */

import { getTenant } from '../tenant';
import { AttioAdapter } from './attio';
import { HubSpotAdapter } from './hubspot';
import { createDemoAdapter } from './demo';
import type { CrmAdapter } from './adapter';
import type { CrmType } from './types';

let _cached: { type: CrmType; adapter: CrmAdapter } | null = null;
let _demoCached: { slug: string; adapter: CrmAdapter } | null = null;

/** Returns the configured adapter, or null if the tenant hasn't connected a CRM yet. */
export function getCrmAdapter(): CrmAdapter | null {
  const t = getTenant().crm.type;
  if (!t) return null;
  if (_cached && _cached.type === t) return _cached.adapter;
  const adapter: CrmAdapter = t === 'hubspot' ? new HubSpotAdapter() : new AttioAdapter();
  _cached = { type: t, adapter };
  return adapter;
}

/**
 * Tenant-aware factory. If `isDemo` is true, returns the demo adapter pinned to
 * the given slug (per-trade canned data). Otherwise falls back to
 * `getCrmAdapter()` which reads the static tenant.config.json.
 *
 * Call this from React contexts that have access to the resolved `v9_tenants`
 * row — the static config-file path doesn't know which tenant is active when
 * the same admin app serves multiple demo subdomains.
 */
export function getAdapterForTenant(opts: { isDemo: boolean; slug: string }): CrmAdapter | null {
  if (opts.isDemo) {
    if (_demoCached && _demoCached.slug === opts.slug) return _demoCached.adapter;
    const adapter = createDemoAdapter(opts.slug);
    _demoCached = { slug: opts.slug, adapter };
    return adapter;
  }
  return getCrmAdapter();
}

/** Force-rebuild the adapter — call after the user switches CRM in Settings. */
export function resetCrmAdapter() {
  _cached = null;
  _demoCached = null;
}

export { HubSpotAdapter } from './hubspot';
export { AttioAdapter } from './attio';
export { AdapterError } from './adapter';
export type { CrmAdapter } from './adapter';
export type {
  Contact,
  CreateContactInput,
  CreateDealInput,
  CrmFilter,
  CrmObjectType,
  CrmType,
  CustomField,
  Deal,
  ListContactsOpts,
  ListContactsResult,
  ListDealsOpts,
  ListDealsResult,
  ListLeadsOpts,
  ListLeadsResult,
  Owner,
  UpdateContactInput,
  UpdateDealPatch,
} from './types';
